import { unparse } from 'papaparse';
import { saveAs } from 'file-saver';
import {
  CellContext,
  RowData,
  TableOptions,
  TableOptionsResolved,
  createTable,
  getCoreRowModel,
} from '@tanstack/react-table';
import { Button } from '../button/button';
import { useLocale } from '@instamenu/context-i18n';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue = unknown> {
    renderCsv?: (props: CellContext<TData, TValue>) => string | number | null | undefined;
  }
}

export type CSVOptions<TData extends RowData> = {
  columns: TableOptions<TData>['columns'];
  data?: TableOptions<TData>['data'];
  filename: string;
};

export default function CSVExportButton<T extends RowData>({
  csvOptions,
}: {
  csvOptions: CSVOptions<T>;
}) {
  const { i18n } = useLocale();
  const handleClick = () => {
    const rows = makeCSVRows(csvOptions);
    const str = unparse(rows);
    const blob = new Blob([str], { type: 'text/csv' });
    saveAs(blob, csvOptions.filename);
  };

  return (
    <Button variant="secondary" onClick={handleClick}>
      {i18n.t('export')}
    </Button>
  );
}

export function makeCSVRows<TData extends RowData>({ columns, data = [] }: CSVOptions<TData>) {
  const resolvedOptions: TableOptionsResolved<TData> = {
    state: {},
    onStateChange: () => undefined,
    renderFallbackValue: null,
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  };

  const table = createTable(resolvedOptions);
  const headerRow = table.getAllLeafColumns().map((column) => column.columnDef.header);

  const dataRows = table.getRowModel().rows.map((row) => {
    return row.getAllCells().map((cell) => {
      return cell.column.columnDef.meta?.renderCsv
        ? cell.column.columnDef.meta?.renderCsv(cell.getContext())
        : cell.renderValue();
    });
  });
  return [headerRow, ...dataRows];
}
